import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { CONNECTION_FAILURE_MESSAGE, isConnectionFailure } from './shared/connectionCopy';
import { useTheme } from './ThemeContext';
import AdminDashboard from './components/AdminDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import ClientDashboardV3 from './components/ClientDashboardV3';
import HomeownerOnboarding from './components/HomeownerOnboarding';
import HomeownerConsentFlow from './components/HomeownerConsentFlow';
import TrialConsentNotice, { SubscribeConsentNotice, subscribeButtonFor, trialButtonFor } from './components/TrialConsentNotice';
import { checkoutPlanLabel } from './shared/planNames';
import HomeownerPlanWelcome from './components/HomeownerPlanWelcome';
import VendorApplicationForm from './components/VendorApplicationForm';
import Footer from './components/Footer';
import { legalPageFromPath, followPolicyLink, setLegalAudience } from './utils/legal';
import { queryPreviewAllowed } from './shared/devPreview';
import SupportPage from './components/SupportPage';
import { devTapToolsEnabled } from './shared/devTapTools';
import { authScreenFromLocation, isKnownAppPath, isHomeownerPlanPath, isHomeownerSignupPath, loginRedirectFromLocation, loginRoleFromSearch, pathAfterSignIn, taskActionFromLocation } from './shared/appPaths';
import { isReportTermsPath, reportShareFromPath } from './shared/homeHistoryReport';
import { claimFromPath } from './shared/homeTransfer';
import HomeHistoryReportView from './components/HomeHistoryReportView';
import HomeTransferClaim from './components/HomeTransferClaim';
import ReportViewerTerms from './components/ReportViewerTerms';
import { homeownerTourStorageKey, readStoredFlag, shouldShowPlanWelcome } from './utils/tourPrompt';
import TaskActionPage from './components/TaskActionPage';
import NotFound from './components/NotFound';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SubscriptionModal from './components/SubscriptionModal';
import { rateLimiter } from './utils/rateLimiter';
import { createCheckoutSession, verifyCheckoutSession, STRIPE_PRICES, TEAM_PRICING } from './utils/stripe';
import { autoAssignTasks } from './utils/taskAssignment';
import {
  getUserTypePreference,
  getClientId,
  setClientId,
  clearClientId,
  getHomeownerToken,
  clearHomeownerToken
} from './utils/storage';
import { loginHomeowner, loadHomeownerClient, loadHomeownerBilling, submitEnterpriseInquiry, touchHomeownerSession } from './utils/homeownerApi';
import { dismissSignupPlan, signupPlanDismissed, signupPlanRequired } from './shared/signupPlanGate';
import HomeownerSignup from './components/HomeownerSignup';
import HomeownerPaywall, { NativePlanMessage } from './components/HomeownerPaywall';
import FriendJoin from './components/FriendJoin';
import { friendJoinFromLocation } from './shared/friendInvite';
import { signInFailureMessage, signInWithEmail } from './shared/emailSignIn';
import { readSetupResume, sessionLostOnLoadError, shouldShowPropertySetup } from './utils/homeownerRestore';
import { subscriptionAllowsAccess } from './utils/subscriptionAccess';
import { canShowPaymentUI, getApiUrl, isNativeApp, NATIVE_PLAN_NOTICE } from './utils/platform';
import { agentAuthHeaders, endAdminEmailSession } from './utils/functionAuth';
import { resetAppScroll } from './utils/appScroll';
import { bindPushUser, clearAppBadge, initializePushNotifications, removeDeviceToken } from './utils/pushNotifications';
import { devLog } from './utils/devLog';
import { ToastContainer, clearToasts, useToast } from './components/Toast';
import TeamSizePicker from './components/TeamSizePicker';
import { stripHomeownerCode } from './shared/homeownerCodeUrl';
import { readIncomingAppUrl } from './shared/appLinks';
import { clearSupabaseAuthToken, signOutAndClearAuthToken } from './utils/authStorage';
import { PROFILE_SETUP_MESSAGE, signupAccountFields, signupAccountStatus } from './shared/agentSignup';
import { rememberTeamInvite } from './shared/inviteLink';
import { checkoutTrialDays, planPickerCopy } from './shared/planSwitchUi';
import { readAuthConfirm } from './shared/authConfirm';
import { authConfirmUrl, MARKETING_SITE } from './shared/siteUrl';
import AuthConfirm from './components/AuthConfirm';
import ExistingAccountNotice from './components/ExistingAccountNotice';
import {
  DELETED_ACCOUNT_MESSAGE,
  readDeletedAccountNotice,
  rememberDeletedAccount,
} from './shared/deletedAccountNotice';
import {
  CHECKOUT_POLL_ATTEMPTS,
  CHECKOUT_POLL_WAIT_MS,
  readCheckoutQuery,
  withoutCheckoutQuery,
  nextCheckoutAction,
  pollSubscriptionProfile,
} from './shared/checkoutReturn';
import './mobile.css';

function PublicChrome({ children, documentScroll = false }) {
  if (documentScroll) {
    return (
      <div className="vendor-invite-chrome">
        {children}
        <Footer />
      </div>
    );
  }
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div style={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
        {children}
      </div>
      <Footer />
    </div>
  );
}

function showPropertySetup(client) {
  if (!client) return false;
  const storage = typeof localStorage === 'undefined' ? null : localStorage;
  return shouldShowPropertySetup(client, { resume: readSetupResume(storage, client.id) });
}

const App = () => {
  const { colors, isDark, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const brandColor = '#59c4c4'; // Pacarion brand color

  // Check for invite code in URL BEFORE initializing state.
  // rememberTeamInvite() reads ?invite= on this render and keeps it after the
  // address bar is cleaned, so the trial box never paints for an invite link.
  const urlInviteCode = rememberTeamInvite();
  const urlHomeownerCode = stripHomeownerCode(
    window.location.pathname + window.location.search + window.location.hash
  ).code;
  const authRoute = authScreenFromLocation(window.location.pathname, window.location.search);
  const urlLoginRole = loginRoleFromSearch(window.location.search);

  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const pendingCheckoutRef = useRef(readCheckoutQuery(window.location.search));
  const checkoutFinishedRef = useRef(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false); // Track if preferences have been loaded
  const [accountDeleted, setAccountDeleted] = useState(readDeletedAccountNotice);
  const [loginMode, setLoginMode] = useState(
    urlInviteCode ? 'signup' : (authRoute ? authRoute.mode : 'signin')
  ); // Start in signup mode for /signup or a team invite
  const [userType, setUserType] = useState(
    urlLoginRole
      ? urlLoginRole.userType
      : (urlHomeownerCode || readDeletedAccountNotice()
        ? 'client'
        : (authRoute ? authRoute.userType : 'agent'))
  ); // agent or client
  const [clientLoginMode, setClientLoginMode] = useState(
    urlLoginRole
      ? urlLoginRole.clientLoginMode
      : ((authRoute && authRoute.clientLoginMode) || 'invite')
  ); // invite or password
  const [client, setClient] = useState(null);
  setLegalAudience({ signedIn: Boolean(session || client) });
  const [homeownerUnlocked, setHomeownerUnlocked] = useState(false);
  const [trialConsent, setTrialConsent] = useState(false);
  const [teamTrialConsent, setTeamTrialConsent] = useState(false);
  const [chargeRefundVariant, setChargeRefundVariant] = useState('prior_charge');
  const [showOnboarding, setShowOnboarding] = useState(false); // Track if showing onboarding flow
  const [awaitingQuestionnaire, setAwaitingQuestionnaire] = useState(false);
  const [showSignupPaywall, setShowSignupPaywall] = useState(false);
  const [inviteCode, setInviteCode] = useState(urlHomeownerCode ? urlHomeownerCode.toUpperCase() : '');
  const [invitePassword, setInvitePassword] = useState('');
  const [teamInviteCode, setTeamInviteCode] = useState(urlInviteCode ? urlInviteCode.toUpperCase() : '');
  const [selectedPlan, setSelectedPlan] = useState(null); // Track selected pricing plan
  const [selectedTeamSize, setSelectedTeamSize] = useState(2); // Default to 2 agents

  useEffect(() => {
    if (!profile || checkoutTrialDays(profile) === 14) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(getApiUrl('/.netlify/functions/billing-customer'), {
          headers: await agentAuthHeaders(),
        });
        const body = await response.json().catch(() => ({}));
        const next = body && body.refundVariant === 'first_charge' ? 'first_charge' : 'prior_charge';
        if (cancelled) return;
        setChargeRefundVariant(next);
        setTrialConsent(false);
        setTeamTrialConsent(false);
      } catch (error) {
        if (!cancelled) setChargeRefundVariant('prior_charge');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile]);
  const [clientFormData, setClientFormData] = useState({
    email: '',
    password: ''
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    firstName: '',
    lastName: '',
    teamName: '',
    phone: '',
    market: ''
  });
  const [signupStep, setSignupStep] = useState('account');
  const [signupError, setSignupError] = useState('');
  const [signInError, setSignInError] = useState('');
  const [clientSignInError, setClientSignInError] = useState('');
  const skipTeamRef = useRef(false);
  const homeownerLogoutRef = useRef(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signupNotice, setSignupNotice] = useState(null);
  const [appPath, setAppPath] = useState(() => (
    typeof window === 'undefined' ? '/' : window.location.pathname
  ));
  const seenAppPath = useRef(null);
  useEffect(() => {
    if (seenAppPath.current == null) {
      seenAppPath.current = appPath;
      return;
    }
    if (seenAppPath.current === appPath) return;
    seenAppPath.current = appPath;
    clearToasts();
  }, [appPath]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordType, setForgotPasswordType] = useState('agent'); // 'agent' or 'client'
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [resetPasswordType, setResetPasswordType] = useState(null);
  const [authConfirmRequest, setAuthConfirmRequest] = useState(() => readAuthConfirm());

  // Subscription modal state
  const [subscriptionModal, setSubscriptionModal] = useState({
    isOpen: false,
    type: null,
    plan: null,
    error: null
  });

  // Enterprise contact modal state
  const [enterpriseModal, setEnterpriseModal] = useState({
    isOpen: false,
    submitting: false
  });

  const [enterpriseFormData, setEnterpriseFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    agentCount: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    const syncLegalPage = () => {
      setAppPath(window.location.pathname);
      const incoming = readIncomingAppUrl(window.location.href);
      if (incoming.homeownerCode) {
        setInviteCode(incoming.homeownerCode.toUpperCase());
        setUserType('client');
        setLoginMode('signin');
      }
      if (incoming.teamInvite) {
        setTeamInviteCode(incoming.teamInvite.toUpperCase());
        setLoginMode('signup');
        setUserType('agent');
      }
      if (incoming.login === 'homeowner') {
        setUserType('client');
        setLoginMode('signin');
      } else if (incoming.login === 'agent') {
        setUserType('agent');
        setLoginMode('signin');
      }
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const type = params.get('type');
      if (token && type) {
        setResetPasswordToken(token);
        setResetPasswordType(type);
      }
    };
    window.addEventListener('popstate', syncLegalPage);
    return () => window.removeEventListener('popstate', syncLegalPage);
  }, []);

  // Screen changes are state, not a router. Drop any leftover window or
  // content-scroller offset so the next screen opens at the top. Legal
  // pages cover this screen and scroll themselves.
  useEffect(() => {
    resetAppScroll();
  }, [
    resetPasswordToken,
    authConfirmRequest,
    showOnboarding,
    client?.id,
    session?.user?.id,
    profile?.role,
    userType,
    loginMode
  ]);

  // Load user preferences and restore session on startup
  useEffect(() => {
    const linkedLogin = loginRoleFromSearch(window.location.search);
    const loadPreferences = async () => {
      try {
        // A direct /login or /signup address, or an email login link, wins
        // over a saved preference. Read the link before any await so a later
        // cleanup of ?login= cannot lose the race.
        if (linkedLogin || authScreenFromLocation(window.location.pathname, window.location.search)) {
          return;
        }
        // Load user type preference (homeowner or agent)
        const savedUserType = await getUserTypePreference();
        if (savedUserType) {
          devLog('Restored user type preference:', savedUserType);

          if (savedUserType === 'homeowner') {
            setUserType('client');

            // Check if they have a stored client ID (were logged in)
            const savedClientId = await getClientId();
            if (savedClientId) {
              devLog('Restoring client session:', savedClientId);
              // Will be loaded by loadClientData below
            }
          } else if (savedUserType === 'agent') {
            setUserType('agent');
            // Supabase auth session will be checked by existing useEffect
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setPreferencesLoaded(true);
      }
    };

    loadPreferences();
  }, []); // Run once on mount

  useEffect(() => {
    const signedIn = Boolean((client && client.id) || (session && profile));
    if (signedIn) document.documentElement.dataset.signedIn = '1';
    else delete document.documentElement.dataset.signedIn;
    if (!signedIn) return undefined;
    const userId = client && client.id
      ? (client.client_user_id || client.id)
      : (session && session.user && session.user.id);
    if (!userId) return undefined;
    initializePushNotifications(userId).catch((error) => {
      console.error('Failed to initialize notifications:', error);
    });
    return undefined;
  }, [client, profile, session]);

  // Define loadClientData BEFORE using it in useEffect
  const applySignupPlanGate = useCallback((data) => {
    const storage = typeof localStorage === 'undefined' ? null : localStorage;
    if (!signupPlanRequired(data, { native: isNativeApp(), storage })) {
      setShowSignupPaywall(false);
      return;
    }
    setShowSignupPaywall(true);
    if (typeof loadHomeownerBilling !== 'function') return;
    loadHomeownerBilling()
      .then((billing) => {
        if (signupPlanDismissed(storage, data && data.id)) return;
        const plan = billing && billing.plan;
        if (plan && plan.entitled) setShowSignupPaywall(false);
      })
      .catch(() => {});
  }, []);

  const loadClientData = useCallback(async () => {
    try {
      let token = await getHomeownerToken();
      if (!token) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        token = await getHomeownerToken();
      }
      // An empty read is not a sign-out. Capacitor Preferences can miss the
      // saved token for a moment after the native shell resumes or reloads.
      if (!token) return;
      const loaded = await loadHomeownerClient();
      const data = loaded && loaded.client;
      if (!data) {
        setClient(null);
        setShowOnboarding(false);
        await clearClientId();
        await clearHomeownerToken();
        return;
      }
      const withJoin = loaded.joinRequest ? { ...data, joinRequest: loaded.joinRequest } : data;

      setClient(withJoin);
      setUserType('client');
      setShowOnboarding(showPropertySetup(withJoin));
      applySignupPlanGate(withJoin);

    } catch (error) {
      console.error('Error loading client data:', error);
      // Only a rejected session signs the homeowner out. A failed setup save
      // or a blip while they go back to the address step must leave the token
      // in place so setup can resume.
      if (!sessionLostOnLoadError(error)) return;
      setClient(null);
      setShowOnboarding(false);
      await clearClientId();
      await clearHomeownerToken();
    }
  }, [applySignupPlanGate]);

  const clientGateId = client && client.id;
  useEffect(() => {
    setHomeownerUnlocked(false);
  }, [clientGateId]);

  const unlockHomeowner = useCallback(() => {
    setHomeownerUnlocked(true);
  }, []);

  // Check if client is already logged in (using Preferences instead of sessionStorage)
  useEffect(() => {
    const checkClientSession = async () => {
      const storedClientId = await getClientId();
      if (storedClientId) {
        loadClientData();
      }
    };

    if (preferencesLoaded) {
      checkClientSession();
    }
  }, [preferencesLoaded, loadClientData]); // Run after preferences are loaded

  // Clean up URL after invite code is read (remove ?invite= from URL bar)
  useEffect(() => {
    if (urlInviteCode) {
      devLog('Team invite code found in URL:', urlInviteCode);
      // Clean up URL without the invite parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop homeowner_code after the login field has read it.
  useEffect(() => {
    const current = window.location.pathname + window.location.search + window.location.hash;
    const stripped = stripHomeownerCode(current);
    if (!stripped.code) return;
    window.history.replaceState({}, document.title, stripped.href);
  }, []);

  // Email links use ?login=homeowner or ?login=agent. Those paths are not routes.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const login = params.get('login');
    if (login !== 'homeowner' && login !== 'agent') return;

    setLoginMode('signin');
    if (login === 'homeowner') {
      setUserType('client');
    } else {
      setUserType('agent');
    }
    params.delete('login');
    const next = params.toString();
    window.history.replaceState({}, document.title, window.location.pathname + (next ? `?${next}` : '') + window.location.hash);
  }, []);

  // Check for password reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const type = params.get('type');

    if (token && type) {
      setResetPasswordToken(token);
      setResetPasswordType(type);
    }
  }, []);

  // Signed-out / used to open the role chooser and the homeowner and agent
  // marketing pages. Those views are gone, so / opens the login screen.
  // Signed-in people stay on / and still reach their dashboard.
  useEffect(() => {
    if (!authReady || loading || !preferencesLoaded) return;
    if (session || client) return;
    if (legalPageFromPath(window.location.pathname) || resetPasswordToken || authConfirmRequest) return;
    const next = loginRedirectFromLocation(window.location.pathname, window.location.search);
    if (!next) return;
    window.history.replaceState({}, '', next);
    setAppPath(next);
  }, [
    authReady,
    loading,
    preferencesLoaded,
    session,
    client,
    resetPasswordToken,
    authConfirmRequest,
  ]);

  useEffect(() => {
    if (!authReady || loading || !preferencesLoaded) return;
    if (!session && !client) return;
    const next = pathAfterSignIn(window.location.pathname);
    if (!next || next === window.location.pathname) return;
    window.history.replaceState({}, '', next + window.location.search + window.location.hash);
    setAppPath(next);
  }, [authReady, loading, preferencesLoaded, session, client]);

  // Hold checkout=success until the Supabase session is loaded, then verify
  // and poll. Leave the query in place until that finishes.
  useEffect(() => {
    const pending = pendingCheckoutRef.current;
    const action = nextCheckoutAction({
      authReady,
      session,
      pending,
      handled: checkoutFinishedRef.current,
    });

    if (action.strip && pending && pending.status === 'canceled' && !checkoutFinishedRef.current) {
      checkoutFinishedRef.current = true;
      handleCheckoutCanceled();
      window.history.replaceState(
        {},
        document.title,
        withoutCheckoutQuery(window.location.pathname + window.location.search + window.location.hash)
      );
      return;
    }

    if (!action.verify || checkoutFinishedRef.current) return;
    checkoutFinishedRef.current = true;
    const userId = session.user.id;
    const sessionId = pending.sessionId;
    handleCheckoutSuccess(sessionId, userId).finally(() => {
      window.history.replaceState(
        {},
        document.title,
        withoutCheckoutQuery(window.location.pathname + window.location.search + window.location.hash)
      );
    });
  }, [authReady, session]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    devLog('Setting up auth listener...');

    // Check active sessions
    let cancelled = false;
    const loadProfileAfterAuth = (userId) => {
      // Queries inside onAuthStateChange run while the auth client still holds
      // its lock, so the request can go out as anon. Profiles are readable
      // without a session; clients are not, and that first read stays empty.
      setTimeout(() => {
        if (!cancelled) loadProfile(userId);
      }, 0);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      devLog('Initial session check:', session ? 'Session exists' : 'No session');
      setSession(session);
      if (session) {
        devLog('Initial session found, loading profile for:', session.user.id);
        loadProfileAfterAuth(session.user.id);
      } else {
        setLoading(false);
      }
    }).catch((error) => {
      console.error('Initial session check failed:', error);
      setLoading(false);
    }).finally(() => {
      if (!cancelled) setAuthReady(true);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      devLog('Auth state changed! Event:', event, 'Session:', session ? 'exists' : 'null');
      setSession(session);
      if (session) {
        devLog('Session detected in auth change, loading profile for:', session.user.id);
        loadProfileAfterAuth(session.user.id);
      } else {
        devLog('No session in auth change, clearing profile');
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Re-check subscription status whenever profile changes (catches back button navigation)
  // Note: Modal is no longer auto-shown - plan buttons are directly on welcome page
  useEffect(() => {
    if (session && profile && !loading) {
      const validStatuses = ['trialing', 'active', 'past_due', 'unpaid'];
      const hasValidSubscription = validStatuses.includes(profile.subscription_status);

      if (!hasValidSubscription && !profile.stripe_customer_id) {
        devLog('Profile loaded - user needs subscription');
        devLog('User will see welcome page with plan selection buttons');
      }
    }
  }, [profile, session, loading]);

  const loadProfile = async (userId, skipSubscriptionCheck = false) => {
    devLog('Loading profile for user_id:', userId);
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      devLog('Profile query result:', { profileData, error });

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Profile query error:', error);
        throw error;
      }

      if (profileData) {
        devLog('Profile found, setting profile state:', profileData);
        setProfile(profileData);

        // Skip subscription check if we're polling for updates or returning from checkout
        if (skipSubscriptionCheck) {
          return profileData;
        }

        // Check if user needs to complete Stripe checkout
        // Valid active subscription statuses (including past_due/unpaid so they can fix payment)
        const validStatuses = ['trialing', 'active', 'past_due', 'unpaid'];
        const hasValidSubscription = validStatuses.includes(profileData.subscription_status);

        if (!hasValidSubscription && !profileData.stripe_customer_id) {
          // User never completed Stripe checkout - will see welcome page with plan buttons
          devLog('User needs to complete subscription setup');
          devLog('Profile status:', {
            subscription_status: profileData.subscription_status,
            stripe_customer_id: profileData.stripe_customer_id,
            hasValidSubscription
          });

          // Check if Stripe is configured
          if (!STRIPE_PRICES.SOLO || STRIPE_PRICES.SOLO === 'undefined') {
            console.error('Stripe not configured - cannot show checkout');
            setSubscriptionModal({
              isOpen: true,
              type: 'subscription-error',
              error: 'Subscription system not configured. Please contact support.',
              plan: null
            });
            setLoading(false);
            return;
          }

          // User will see welcome page with plan selection buttons (no modal needed)
          devLog('User will see welcome page with plan buttons');
        }
      } else {
        devLog('No profile found for user_id:', userId);
      }
      return profileData;
    } catch (error) {
      console.error('❌ Error loading profile:', error);
      return null;
    } finally {
      devLog('Setting loading to false');
      setLoading(false);
    }
  };

  // Poll for the trial the verify call just wrote. A few reads, not a long webhook wait.
  const pollForSubscription = (userId, maxAttempts = CHECKOUT_POLL_ATTEMPTS, waitMs = CHECKOUT_POLL_WAIT_MS) => {
    return pollSubscriptionProfile(
      () => loadProfile(userId, true),
      {
        attempts: maxAttempts,
        waitMs,
        allows: (profileData) => {
          if (profileData && subscriptionAllowsAccess(profileData)) {
            devLog('Subscription found!', profileData.subscription_status);
            return true;
          }
          return false;
        },
      }
    );
  };

  // Handle checkout success once the Supabase session exists.
  const handleCheckoutSuccess = async (checkoutSessionId, userId) => {
    if (!userId) {
      devLog('No session found after checkout');
      return;
    }

        devLog('Checkout completed successfully, checking the trial...');

    // Show loading modal
    setSubscriptionModal({
      isOpen: true,
      type: 'loading-subscription',
      plan: null,
      error: null
    });

    try {
      if (checkoutSessionId) {
        try {
          await verifyCheckoutSession(checkoutSessionId);
        } catch (verifyError) {
          console.error('Checkout verify failed:', verifyError);
        }
      }
      const updatedProfile = await pollForSubscription(userId);

      if (updatedProfile && subscriptionAllowsAccess(updatedProfile)) {
        devLog('Subscription is active, showing dashboard');
        setSubscriptionModal({ isOpen: false, type: null, plan: null, error: null });
        setProfile(updatedProfile);
      } else {
        // Timeout - webhook didn't fire in time
        console.error('Timeout waiting for the subscription');
        setSubscriptionModal({
          isOpen: true,
          type: 'subscription-error',
          error: 'We\'re still processing your subscription. This may take a minute.\n\nYou can sign out and sign back in, or contact support if this continues.',
          plan: null
        });
      }
    } catch (error) {
      console.error('Error polling for subscription:', error);
      setSubscriptionModal({
        isOpen: true,
        type: 'subscription-error',
            error: 'There was an error checking your subscription. Please contact support.',
        plan: null
      });
    }
  };

  // Handle checkout canceled
  const handleCheckoutCanceled = () => {
    devLog('User canceled checkout');

    setSubscriptionModal({
      isOpen: true,
      type: 'checkout-canceled',
      plan: null,
      error: null
    });
  };

  // Handle modal plan selection
  const handleModalPlanSelection = async (planChoice, teamSize = null) => {
    if (!session || !profile) {
      console.error('No session or profile found');
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      devLog('Plan selection', planChoice, teamSize);
    }

    let priceId;
    if (planChoice === 'solo') {
      priceId = STRIPE_PRICES.SOLO;
    } else if (planChoice === 'team' && teamSize) {
      // Find the price ID for the selected team size
      const teamPlan = TEAM_PRICING.find(t => t.agents === teamSize);
      if (!teamPlan) {
        showToast('Invalid team size selected');
        return;
      }
      priceId = teamPlan.priceId;
    } else {
      showToast('Please select a valid plan');
      return;
    }

    // iOS Reader App: Prevent payment UI (Apple App Store compliance)
    if (!canShowPaymentUI()) {
      setSubscriptionModal({
        isOpen: true,
        type: 'subscription-error',
        error: NATIVE_PLAN_NOTICE,
        plan: null
      });
      return;
    }

    const trialAgreed = planChoice === 'team' ? teamTrialConsent : trialConsent;
    const offerTrial = checkoutTrialDays(profile) === 14;
    if (!trialAgreed) {
      showToast(offerTrial
        ? 'Check the box to agree to the free trial and automatic monthly charge before continuing.'
        : 'Check the box to agree to the monthly charge before continuing.');
      return;
    }

    setSubscriptionModal({
      isOpen: true,
      type: offerTrial ? 'starting-trial' : 'starting-checkout',
      plan: planChoice,
      agents: planChoice === 'team' ? selectedTeamSize : 1,
      error: null
    });

    try {
      await createCheckoutSession({
        priceId: priceId,
        agentId: profile.id,
        email: profile.email,
        consent: {
          autoRenewal: true,
          kind: offerTrial ? 'trial' : 'subscribe',
          refundVariant: offerTrial ? undefined : chargeRefundVariant,
        },
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setSubscriptionModal({
        isOpen: true,
        type: 'subscription-error',
        error: 'Unable to start checkout. Please try again or contact support.',
        plan: null
      });
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setSubscriptionModal({ isOpen: false, type: null, plan: null, error: null });
  };

  // Handle Enterprise form submission
  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!enterpriseFormData.firstName || !enterpriseFormData.lastName ||
        !enterpriseFormData.email || !enterpriseFormData.company ||
        !enterpriseFormData.agentCount) {
      showToast('Please fill in all required fields');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enterpriseFormData.email)) {
      showToast('Please enter a valid email address');
      return;
    }

    // Validate agent count
    const agentCount = parseInt(enterpriseFormData.agentCount);
    if (isNaN(agentCount) || agentCount < 11) {
      showToast('Enterprise plans are for 10+ agents. Please enter at least 11 agents.');
      return;
    }

    setEnterpriseModal({ ...enterpriseModal, submitting: true });

    try {
      // Save inquiry to database
      await submitEnterpriseInquiry({
        first_name: enterpriseFormData.firstName,
        last_name: enterpriseFormData.lastName,
        email: enterpriseFormData.email,
        company: enterpriseFormData.company,
        agent_count: agentCount,
        phone: enterpriseFormData.phone,
        message: enterpriseFormData.message
      });

      // Success!
      showToast('Thank you for your interest in Pacarion Enterprise!\n\nOur team will contact you within 24 hours to discuss your custom pricing and setup.');

      // Reset form and close modal
      setEnterpriseFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        agentCount: '',
        phone: '',
        message: ''
      });
      setEnterpriseModal({ isOpen: false, submitting: false });

    } catch (error) {
      console.error('Error submitting enterprise inquiry:', error);
      showToast('Error submitting inquiry. Please try again or email us directly at enterprise@pacarion.com');
      setEnterpriseModal({ ...enterpriseModal, submitting: false });
    }
  };

  const openHomeownerSession = async (result, rateKey) => {
    const data = result.client;
    if (result.resetRequired) {
      showToast('This password has to be reset. Choose a new one on the next screen.');
    } else if (result.passwordSetup) {
      showToast('No password set. Please use your invite code to set one.');
      setLoading(false);
      return;
    }
    if (rateKey) rateLimiter.recordAttempt(rateKey, true);
    setUserType('client');
    await setClientId(data.id);
    setClient(data);
    setShowOnboarding(showPropertySetup(data));
    applySignupPlanGate(data);
    setLoading(false);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignInError('');
    devLog('Agent sign in attempted');
    const email = String(formData.email || '').trim();
    const password = formData.password;

    try {
      const outcome = await signInWithEmail({
        email,
        password,
        signInAgent: (agentEmail, agentPassword) => supabase.auth.signInWithPassword({
          email: agentEmail,
          password: agentPassword,
        }),
      });

      if (outcome.kind === 'error') {
        console.error('❌ Sign in error:', outcome.error);
        throw outcome.error;
      }

      const data = outcome.result && outcome.result.data;
      devLog('Sign in successful:', data);
      if (teamInviteCode.trim()) {
        const response = await fetch(getApiUrl('/.netlify/functions/profile-entitlements'), {
          method: 'POST',
          headers: await agentAuthHeaders(),
          body: JSON.stringify({
            action: 'join-team',
            inviteCode: teamInviteCode.trim().toUpperCase(),
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          showToast(result.error || 'You are signed in, but the team invite could not be used.');
        } else {
          showToast(result.teamName ? `You joined ${result.teamName}.` : 'You joined the team.');
        }
        setTeamInviteCode('');
      }
      // Profile will be loaded by the onAuthStateChange listener
    } catch (error) {
      console.error('Error signing in:', error);
      const credentialError = error && (Number(error.status) === 400 || Number(error.status) === 401);
      const message = !credentialError && isConnectionFailure(error)
        ? CONNECTION_FAILURE_MESSAGE
        : signInFailureMessage(error);
      setSignInError(message);
      if (message === CONNECTION_FAILURE_MESSAGE) showToast(message, 'error');
    }
  };

  const handleSignupAccount = (e) => {
    e.preventDefault();
    setSignupNotice(null);
    const fields = signupAccountFields(formData);
    if (!fields.ok) {
      if (fields.errors.includes('password')) setSignupError('Use a password of at least 8 characters.');
      else if (fields.errors.includes('name')) setSignupError('Enter your name.');
      else if (fields.errors.includes('market')) setSignupError('Enter your market.');
      else setSignupError('Enter your email.');
      return;
    }
    if (!agreedToTerms) {
      setSignupError('Agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    if (!urlInviteCode && !trialConsent) {
      setSignupError('Check the box to agree to the free trial and automatic monthly charge before continuing.');
      return;
    }
    setSignupError('');
    setFormData((current) => ({
      ...current,
      firstName: fields.name.firstName,
      lastName: fields.name.lastName,
      email: fields.email,
      market: fields.market,
    }));
    setSignupStep('team');
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const skipTeam = skipTeamRef.current || Boolean(e.nativeEvent && e.nativeEvent.submitter && e.nativeEvent.submitter.name === 'skip-team');
    skipTeamRef.current = false;
    const inviteCodeValue = skipTeam ? '' : teamInviteCode.trim();
    const teamName = skipTeam ? '' : formData.teamName.trim();

    // Check if user agreed to terms
    if (!agreedToTerms) {
      setSignupError('Agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    const fields = signupAccountFields({ ...formData, name: formData.name || `${formData.firstName} ${formData.lastName}` });
    if (!fields.ok) {
      setSignupStep('account');
      setSignupError(fields.errors.includes('password')
        ? 'Use a password of at least 8 characters.'
        : 'Check your name, email, and market.');
      return;
    }
    setSignupNotice(null);
    setSignupError('');

    try {
      // The invite row is not readable before sign-in, so the function checks it.
      if (inviteCodeValue) {
        try {
          const response = await fetch(getApiUrl('/.netlify/functions/signup-with-team-invite'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: fields.email,
              password: fields.password,
              firstName: fields.name.firstName,
              lastName: fields.name.lastName,
              teamName,
              phone: '',
              market: fields.market,
              inviteCode: inviteCodeValue
            })
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.error || 'Team signup failed');
          }

          devLog('Successfully joined team!', result);
          showToast(result.message || 'Welcome to the team! You can now sign in.');

          setTeamInviteCode('');
          setLoginMode('signin');
          setAgreedToTerms(false);
          setSelectedPlan(null);
          return; // Exit early, user needs to sign in

        } catch (teamError) {
          console.error('Error joining team:', teamError);
          showToast('Error joining team: ' + teamError.message);
          return;
        }
      }

      // For non-team signups, create auth user with metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: fields.email,
        password: fields.password,
        options: {
          emailRedirectTo: authConfirmUrl(window.location),
          data: {
            first_name: fields.name.firstName,
            last_name: fields.name.lastName,
            team_name: teamName,
            phone: '',
            market: fields.market
          }
        }
      });

      const accountStatus = signupAccountStatus(authData, authError);
      if (accountStatus === 'existing') {
        setSignupNotice('existing');
        return;
      }
      if (authError) throw authError;
      if (accountStatus !== 'created') {
        throw new Error('Could not create the account.');
      }

      if (selectedPlan && authData?.user && !authData.session) {
        showToast('Account created! Check your email and tap the link. Then continue to sign in or checkout.');
        setLoginMode('signin');
        setAgreedToTerms(false);
        setSelectedPlan(null);
        return;
      }

      if (selectedPlan && authData?.user) {
        // If a plan was selected and user was created, redirect to Stripe checkout
        devLog('Account created! User ID:', authData.user.id);
        devLog('Selected plan:', selectedPlan);

        const priceId = selectedPlan === 'solo' ? STRIPE_PRICES.SOLO : STRIPE_PRICES.TEAM;
        devLog('Stripe checkout starting');

        // Check if Stripe is configured
        if (!priceId || priceId === 'undefined') {
          console.error('Stripe not configured - Price ID is missing');
          showToast('Account created, but Stripe checkout is not configured. Please contact support.');
          setLoginMode('signin');
          setAgreedToTerms(false);
          setSelectedPlan(null);
          return;
        }

        try {
          devLog('Showing trial modal and preparing Stripe checkout...');

          // Show starting trial modal
          setSubscriptionModal({
            isOpen: true,
            type: 'starting-trial',
            plan: selectedPlan,
            agents: selectedPlan === 'team' ? selectedTeamSize : 1,
            error: null
          });

          // Wait for database trigger to create profile
          devLog('Waiting for profile to be created by database trigger...');
          let profileData = null;
          for (let i = 0; i < 10; i++) {
            const { data, error } = await supabase
              .from('profiles')
              .select('id, email')
              .eq('user_id', authData.user.id)
              .maybeSingle();

            if (data) {
              profileData = data;
              devLog('Profile created! Agent ID:', profileData.id);
              break;
            }
            if (error && error.code !== 'PGRST116') {
              console.error('Profile query error:', error);
            }
            devLog(`Waiting for profile... attempt ${i + 1}/10`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          if (!profileData) {
            setSubscriptionModal({
              isOpen: true,
              type: 'account-ready',
              error: PROFILE_SETUP_MESSAGE,
              plan: null
            });
            setLoginMode('signin');
            setAgreedToTerms(false);
            setSelectedPlan(null);
            return;
          }

          devLog('Redirecting to Stripe checkout');

          // iOS Reader App: Prevent payment UI (Apple App Store compliance)
          if (!canShowPaymentUI()) {
            throw new Error(NATIVE_PLAN_NOTICE);
          }

          // Redirect to Stripe checkout
          await createCheckoutSession({
            priceId: priceId,
            agentId: profileData.id,
            email: formData.email,
            consent: trialConsent ? { autoRenewal: true, kind: 'trial' } : null,
          });
        } catch (stripeError) {
          console.error('Stripe checkout error:', stripeError);
          const nativeBlocked = stripeError && stripeError.message === NATIVE_PLAN_NOTICE;
          setSubscriptionModal({
            isOpen: true,
            type: 'subscription-error',
            error: nativeBlocked
              ? NATIVE_PLAN_NOTICE
              : 'Account created but checkout failed. Please sign in and try again.',
            plan: null
          });
          setLoginMode('signin');
          setAgreedToTerms(false);
          setSelectedPlan(null);
        }
      } else {
        // No plan selected and no team invite - database trigger will create profile
        devLog('Account created! Profile will be created by database trigger.');
        const planPickerShowing = Boolean(authData?.session);
        showToast(
          planPickerShowing
            ? 'Account created! Please check your email and tap the link.'
            : 'Account created! Please check your email and tap the link, then sign in.',
          'success'
        );
        if (!planPickerShowing) setLoginMode('signin');
        setAgreedToTerms(false);
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Error signing up:', error);
      showToast(
        isConnectionFailure(error)
          ? CONNECTION_FAILURE_MESSAGE
          : 'Error signing up: ' + error.message,
        isConnectionFailure(error) ? 'error' : undefined
      );
    }
  };

  const handleClientLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const trimmedCode = inviteCode.toUpperCase().trim();
    devLog('Homeowner login attempt with invite code');

    // Check rate limit
    const rateCheck = rateLimiter.checkLimit(trimmedCode);
    if (!rateCheck.allowed) {
      showToast(rateCheck.message);
      setLoading(false);
      return;
    }

    try {
      const result = await loginHomeowner({
        inviteCode: trimmedCode,
        password: invitePassword
      });
      const data = result.client;
      if (result.resetRequired) {
        showToast('This password has to be reset before you can continue. Choose a new one on the next screen.');
        setShowOnboarding(false);
      }

      if (result.firstLogin) {
        try {
          const tasksAssigned = await autoAssignTasks(
            data.id,
            data.agent_id,
            data.property_type || 'single_family',
            data.features || []
          );
          devLog(`Task assignment check assigned ${tasksAssigned} tasks`);
        } catch (assignError) {
          console.error('Error auto-assigning tasks on login:', assignError);
        }
      }

      rateLimiter.recordAttempt(trimmedCode, true);
      await setClientId(data.id);
      setClient(data);
      setShowOnboarding(showPropertySetup(data));
      applySignupPlanGate(data);
      setLoading(false);
    } catch (error) {
      console.error('Client login error:', error);
      if (!isConnectionFailure(error)) rateLimiter.recordAttempt(trimmedCode, false);
      showToast(
        isConnectionFailure(error)
          ? CONNECTION_FAILURE_MESSAGE
          : (error.message || 'Invalid invite code. Please check and try again.'),
        isConnectionFailure(error) ? 'error' : undefined
      );
      setLoading(false);
    }
  };

  const handleClientPasswordLogin = async (e) => {
    e.preventDefault();
    setClientSignInError('');
    setLoading(true);

    const trimmedEmail = clientFormData.email.toLowerCase().trim();
    devLog('Homeowner password login attempt');

    // Check rate limit
    const rateCheck = rateLimiter.checkLimit(trimmedEmail);
    if (!rateCheck.allowed) {
      setClientSignInError(rateCheck.message || 'That email or password is incorrect.');
      showToast(rateCheck.message);
      setLoading(false);
      return;
    }

    try {
      const result = await loginHomeowner({
        email: trimmedEmail,
        password: clientFormData.password
      });
      await openHomeownerSession(result, trimmedEmail);
    } catch (error) {
      console.error('Client password login error:', error);
      const credentialError = error && (Number(error.status) === 400 || Number(error.status) === 401);
      if (!isConnectionFailure(error) || credentialError) rateLimiter.recordAttempt(trimmedEmail, false);
      const message = !credentialError && isConnectionFailure(error)
        ? CONNECTION_FAILURE_MESSAGE
        : 'That email or password is incorrect.';
      setClientSignInError(message);
      if (message === CONNECTION_FAILURE_MESSAGE) showToast(message, 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    const signedIn = Boolean((client && client.id) || (session && profile));
    if (signedIn) clearAppBadge();
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if ((client && client.id) || (session && profile)) clearAppBadge();
      touchHomeownerSession().then((result) => {
        if (result && result.unauthorized && homeownerLogoutRef.current) {
          homeownerLogoutRef.current();
        }
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [client, profile, session]);

  const handleLogout = async (reason) => {
    setFormData({
      email: '',
      password: '',
      name: '',
      firstName: '',
      lastName: '',
      teamName: '',
      phone: '',
      market: '',
    });
    setClientFormData({ email: '', password: '' });
    setSignInError('');
    setClientSignInError('');
    setInviteCode('');
    setInvitePassword('');
    try {
      await endAdminEmailSession();
    } catch (error) {
      console.error('Admin sign-in session clear failed', error);
    }
    const pushUserId = userType === 'client'
      ? (client && (client.client_user_id || client.id))
      : (session && session.user && session.user.id);
    try {
      if (pushUserId) await removeDeviceToken(pushUserId);
    } catch (error) {
      console.error('Error removing device token:', error);
    }
    try {
      await bindPushUser(null);
    } catch (error) {
      console.error('Error clearing push user:', error);
    }
    if (reason === 'deleted') {
      rememberDeletedAccount();
      setAccountDeleted(true);
      setUserType('client');
      setLoginMode('signin');
    }
    try {
      if (userType === 'client') {
        // Clear client ID but keep the homeowner or agent login preference.
        await clearClientId();
        await clearHomeownerToken();
        setClient(null);
        setShowOnboarding(false);
        setInviteCode('');
        // Don't clear userType or user type preference - they're still a homeowner
      }

      // Local scope revokes only this phone. The other phone keeps its session.
      // Always drop sb-*-auth-token from WebView storage and iOS Preferences.
      await signOutAndClearAuthToken(supabase);
      setSession(null);
      setProfile(null);
    } catch (error) {
      clearSupabaseAuthToken(window.localStorage);
      try {
        const { clearNativeSupabaseAuth } = await import('./utils/supabaseAuthStorage');
        await clearNativeSupabaseAuth();
      } catch (clearError) {
        // WebView storage is already cleared.
      }
      setSession(null);
      setProfile(null);
      console.error('Error logging out:', error);
    }
  };
  homeownerLogoutRef.current = handleLogout;

  // Show password reset page if token is present
  if (resetPasswordToken && resetPasswordType) {
    return (
      <PublicChrome>
        <ResetPassword
          token={resetPasswordToken}
          type={resetPasswordType}
          onBack={() => {
            setResetPasswordToken(null);
            setResetPasswordType(null);
            window.history.pushState({}, '', '/');
          }}
        />
      </PublicChrome>
    );
  }

  if (authConfirmRequest) {
    return (
      <PublicChrome>
        <AuthConfirm
          request={authConfirmRequest}
          onContinue={() => {
            window.history.pushState({}, '', '/');
            setAuthConfirmRequest(null);
          }}
        />
      </PublicChrome>
    );
  }

  const supportPath = String(appPath || '').split('?')[0].replace(/\/+$/, '') || '/';
  if (supportPath === '/tap-check' && devTapToolsEnabled(process.env)) {
    const TapCheckPage = require('./components/TapCheckPage').default;
    return <TapCheckPage />;
  }
  if (supportPath === '/support') {
    return <SupportPage />;
  }

  const reportShare = reportShareFromPath(appPath);
  if (reportShare) {
    return (
      <PublicChrome>
        <HomeHistoryReportView token={reportShare.token} />
      </PublicChrome>
    );
  }
  const homeClaim = claimFromPath(appPath);
  if (homeClaim) {
    return (
      <PublicChrome>
        <HomeTransferClaim token={homeClaim.token} />
      </PublicChrome>
    );
  }
  if (isReportTermsPath(appPath)) {
    return <ReportViewerTerms />;
  }

  const taskAction = taskActionFromLocation(appPath, window.location.search);
  if (taskAction) {
    return (
      <PublicChrome>
        <TaskActionPage
          taskId={taskAction.taskId}
          action={taskAction.action}
          token={taskAction.token}
          scope={taskAction.scope}
        />
      </PublicChrome>
    );
  }

  const joinPath = String(appPath || '').split('?')[0].replace(/\/+$/, '') || '/';
  if (joinPath === '/join') {
    const joinRef = friendJoinFromLocation(appPath, window.location.search);
    return (
      <PublicChrome>
        <FriendJoin
          token={joinRef}
          native={isNativeApp()}
          signedIn={Boolean(client)}
          onSignIn={() => {
            window.history.pushState({}, '', '/login');
            setAppPath('/login');
            setUserType('client');
            setClientLoginMode('password');
          }}
          onStartOwn={() => {
            if (isNativeApp()) return;
            window.history.pushState({}, '', '/homeowner/plan');
            setAppPath('/homeowner/plan');
          }}
          onSignedUp={async (result) => {
            const data = result && result.client;
            if (data) {
              const next = result.joinRequest ? { ...data, joinRequest: result.joinRequest } : data;
              await setClientId(data.id);
              setClient(next);
              setUserType('client');
              const sponsored = Boolean(data.agent_id || data.assigned_agent_id || result.joinRequest);
              setAwaitingQuestionnaire(false);
              setShowSignupPaywall(!sponsored && !isNativeApp());
              setShowOnboarding(sponsored ? showPropertySetup(data) : false);
            }
            window.history.pushState({}, '', '/');
            setAppPath('/');
          }}
        />
      </PublicChrome>
    );
  }

  if (!isKnownAppPath(appPath)) {
    return (
      <PublicChrome>
        <NotFound
          onHome={() => {
            window.history.pushState({}, '', '/login');
            setAppPath('/login');
          }}
        />
      </PublicChrome>
    );
  }

  // Query previews are development builds only. The native app's host is
  // localhost, so the hostname is not a reason to mount one.
  const queryPreview = process.env.NODE_ENV === 'development'
    && queryPreviewAllowed(process.env.NODE_ENV, isNativeApp())
    && typeof window !== 'undefined';

  if (queryPreview && window.location.search.indexOf('preview=homeowner-account') !== -1) {
    const HomeownerAccountPreview = require('./components/HomeownerAccountPreview').default;
    return <HomeownerAccountPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=agent-account') !== -1) {
    const Settings = require('./components/Settings').default;
    return (
      <Settings
        profile={{
          id: 'preview-agent',
          email: 'ada@example.com',
          first_name: 'Ada',
          last_name: 'Chen',
          subscription_status: 'active',
          subscription_plan: 'solo',
        }}
        onLogout={() => {}}
      />
    );
  }

  if (queryPreview && window.location.search.indexOf('preview=client-invited') !== -1) {
    const ClientInvitePreview = require('./components/ClientInvitePreview').default;
    return <ClientInvitePreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=vendor-invite') !== -1) {
    return (
      <PublicChrome documentScroll>
        <VendorApplicationForm
          token=""
          sponsor={{
            first_name: 'Mina',
            last_name: 'Chen',
            brand_color: '#9a3412',
            logo_url: '/images/logo/pacarion-logo-light.png',
            headshot_url: '/images/logo/pacarion-logo-dark.png',
          }}
        />
      </PublicChrome>
    );
  }

  const vendorInviteToken = new URLSearchParams(window.location.search).get('vendorInvite');
  if (vendorInviteToken) {
    return (
      <PublicChrome documentScroll>
        <VendorApplicationForm token={vendorInviteToken} />
      </PublicChrome>
    );
  }

  if (queryPreview && window.location.search.indexOf('preview=team') !== -1) {
    const TeamSettingsPreview = require('./components/TeamSettingsPreview').default;
    return <TeamSettingsPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=invite-friend') !== -1) {
    const InviteFriendPreview = require('./components/InviteFriendPreview').default;
    return <InviteFriendPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=agent-home') !== -1) {
    const AgentHomePreview = require('./components/AgentHomePreview').default;
    return <AgentHomePreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=ai-chat') !== -1) {
    const AiChatPreview = require('./components/AiChatPreview').default;
    return <AiChatPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=homeowner-assistant') !== -1) {
    const HomeownerAssistantPreview = require('./components/HomeownerAssistantPreview').default;
    return <HomeownerAssistantPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=move') !== -1) {
    const MovePreview = require('./components/MovePreview').default;
    return <MovePreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=task-help') !== -1) {
    const TaskHelpPreview = require('./components/TaskHelpPreview').default;
    return <TaskHelpPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=homeowner-signup-plan') !== -1) {
    return (
      <HomeownerPaywall
        signup
        native={false}
        plan={{ reason: 'unpaid', entitled: false }}
        onClose={() => {}}
      />
    );
  }

  if (queryPreview && window.location.search.indexOf('preview=homeowner-billing') !== -1) {
    const HomeownerBillingPreview = require('./components/HomeownerBillingPreview').default;
    return <HomeownerBillingPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=home-transfer') !== -1) {
    const HomeTransferPreview = require('./components/HomeTransferPreview').default;
    return <HomeTransferPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=setup-item') !== -1) {
    const SetupItemPreview = require('./components/SetupItemPreview').default;
    return <SetupItemPreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=plan-welcome') !== -1) {
    const PlanWelcomePreview = require('./components/PlanWelcomePreview').default;
    return <PlanWelcomePreview />;
  }

  if (queryPreview && window.location.search.indexOf('preview=imports') !== -1) {
    const ImportPreview = require('./components/ImportPreview').default;
    return <ImportPreview />;
  }

  if (client && awaitingQuestionnaire && homeownerUnlocked) {
    return (
      <HomeownerPlanWelcome
        client={client}
        onDone={async () => {
          setAwaitingQuestionnaire(false);
          await loadClientData(client.id);
        }}
        onLogout={handleLogout}
      />
    );
  }

  if (client && showSignupPaywall && homeownerUnlocked && !isNativeApp()) {
    return (
      <HomeownerPaywall
        native={false}
        plan={{ reason: 'unpaid', entitled: false }}
        signup
        onClose={() => {
          dismissSignupPlan(typeof localStorage === 'undefined' ? null : localStorage, client.id);
          setShowSignupPaywall(false);
          setShowOnboarding(true);
          window.history.pushState({}, '', '/');
          setAppPath('/');
        }}
      />
    );
  }

  if (client && showSignupPaywall && homeownerUnlocked && isNativeApp()) {
    return (
      <HomeownerPaywall
        native
        plan={{ reason: 'unpaid', entitled: false }}
        onClose={() => setShowSignupPaywall(false)}
      />
    );
  }

  // Invite clickwrap, then a one-time terms update, both before the dashboard.
  if (client && !homeownerUnlocked) {
    return (
      <HomeownerConsentFlow
        client={client}
        onClient={setClient}
        onUnlocked={unlockHomeowner}
        onLogout={handleLogout}
      />
    );
  }

  // After the invite password, a short plan step. The long property wizard
  // stays off once the account exists, so this is the path that asks.
  const planWelcomeStored = client
    ? readStoredFlag(
      typeof localStorage !== 'undefined' ? localStorage : null,
      homeownerTourStorageKey(client.id)
    )
    : null;
  if (shouldShowPlanWelcome(client, planWelcomeStored)) {
    return (
      <HomeownerPlanWelcome
        client={client}
        onDone={async () => {
          const sponsored = Boolean(client.agent_id || client.assigned_agent_id);
          const request = client.joinRequest;
          const waiting = request && (request.status === 'pending' || request.status === 'declined');
          if (!sponsored && !waiting) setShowSignupPaywall(true);
          await loadClientData(client.id);
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Show Onboarding if client is logged in but hasn't completed onboarding
  if (client && showOnboarding) {
    return (
      <HomeownerOnboarding
        client={client}
        resumeSetup={readSetupResume(typeof localStorage === 'undefined' ? null : localStorage, client.id)}
        onComplete={async () => {
          // Reload client data after onboarding completes
          await loadClientData(client.id);
          setShowOnboarding(false);
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Show Client Dashboard if client is logged in and has completed onboarding
  if (client && isHomeownerPlanPath(appPath)) {
    if (isNativeApp()) {
      return (
        <div className="app-scroll app-scroll-center" style={{ ...styles.container, background: colors.background, padding: '1.5rem' }}>
          <NativePlanMessage colors={colors} />
        </div>
      );
    }
    return (
      <HomeownerPaywall
        native={false}
        onClose={() => {
          window.history.pushState({}, '', '/');
          setAppPath('/');
        }}
      />
    );
  }

  if (client && !showOnboarding) {
    return (
      <ClientDashboardV3
        client={client}
        userId={client.client_user_id || client.id}
        onLogout={handleLogout}
        onClientReplaced={(next) => {
          if (!next || !next.id) return;
          setClient(next);
          setClientId(next.id);
          setShowOnboarding(showPropertySetup(next));
        }}
        onTourFinished={() => {
          setClient((current) => (current ? { ...current, tutorial_completed: true } : current));
        }}
      />
    );
  }

  // Show Admin Dashboard if agent is logged in AND has valid subscription
  if (session && profile) {
    // Super Admin bypass - always show Super Admin Dashboard
    if (profile.role === 'super_admin') {
      devLog('Super Admin detected - showing Super Admin Dashboard');
      return (
        <SuperAdminDashboard
          profile={profile}
          onLogout={handleLogout}
        />
      );
    }

    // trialing, active, or past_due within 7 days of the period end.
    // A stripe_customer_id alone does not open the dashboard.
    const hasValidSubscription = subscriptionAllowsAccess(profile);

    if (hasValidSubscription) {
      devLog('Showing dashboard - subscription valid');
      return (
        <AdminDashboard
          profile={profile}
          onLogout={handleLogout}
          onProfileUpdate={(patch) => {
            setProfile((current) => (current ? { ...current, ...patch } : current));
          }}
        />
      );
    }

    // If no valid subscription, show welcome page with modal for plan selection
    // This creates a loop: welcome page → modal → Stripe → back button → welcome page
    devLog('No valid subscription - showing welcome page with plan selection modal');
    devLog('Current subscription status:', {
      subscription_status: profile.subscription_status,
      stripe_customer_id: profile.stripe_customer_id,
      hasValidSubscription
    });

    // Show a welcome page with plan selection buttons
    const offerNewTrial = checkoutTrialDays(profile) === 14;
    const pickerCopy = planPickerCopy(profile);
    const pickerAddon = profile.homeowner_addon;
    return (
      <div className="app-scroll app-scroll-center plan-picker" style={{...styles.container, background: colors.background, justifyContent: 'flex-start'}}>
        <div style={{
          ...styles.loginBox,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          textAlign: 'center',
          padding: '3rem',
          maxWidth: '550px'
        }}>
          <h1 style={{...styles.title, color: colors.text, marginBottom: '1rem'}}>
            Welcome to Pacarion!
          </h1>
          {pickerCopy.intro ? (
            <p style={{color: colors.textSecondary, fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem'}}>
              {pickerCopy.intro}
            </p>
          ) : null}
          {isNativeApp() ? (
            <p style={{color: colors.text, fontSize: '1rem', lineHeight: 1.6, marginBottom: '2rem'}}>
              {NATIVE_PLAN_NOTICE}
            </p>
          ) : (
          <>
          <p style={{color: colors.text, fontSize: '0.95rem', marginBottom: '2rem'}}>
            Please select a plan to continue to your dashboard.
          </p>

          {/* Plan Selection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
            {/* Solo Plan */}
            <div style={{
              padding: '1.25rem',
              borderRadius: '12px',
              border: `2px solid ${colors.border}`,
              background: colors.background
            }}>
              <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: '0.5rem', color: colors.text }}>
                {checkoutPlanLabel('solo', 1)} - $49/month
              </div>
              <div style={{ fontSize: '0.9rem', marginBottom: '1rem', color: colors.textSecondary }}>
                200 homeowners • Perfect for individual agents
              </div>
              {offerNewTrial ? (
                <div style={{ marginBottom: '1rem' }}>
                  <TrialConsentNotice
                    planName="solo"
                    agents={1}
                    profile={profile}
                    checked={trialConsent}
                    onChange={setTrialConsent}
                    colors={colors}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <SubscribeConsentNotice
                    planName="solo"
                    agents={1}
                    addon={pickerAddon}
                    refundVariant={chargeRefundVariant}
                    checked={trialConsent}
                    onChange={setTrialConsent}
                    colors={colors}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => handleModalPlanSelection('solo')}
                disabled={!trialConsent}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`,
                  color: 'white',
                  cursor: !trialConsent ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  opacity: !trialConsent ? 0.55 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 16px rgba(89, 196, 196, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {offerNewTrial
                  ? trialButtonFor({ planName: 'solo', agents: 1, profile })
                  : subscribeButtonFor({ planName: 'solo', agents: 1, addon: pickerAddon })}
              </button>
            </div>

            {/* Team Plan with Size Selection */}
            <div style={{
              padding: '1.25rem',
              borderRadius: '12px',
              border: `2px solid ${brandColor}`,
              background: colors.background
            }}>
              <div style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: '0.5rem', color: colors.text }}>
                {checkoutPlanLabel('team', selectedTeamSize)}
              </div>
              <div style={{ fontSize: '0.9rem', marginBottom: '1rem', color: colors.textSecondary }}>
                Choose your team size (2-10 agents)
              </div>

              <TeamSizePicker
                value={selectedTeamSize}
                options={TEAM_PRICING}
                onChange={(size) => {
                  setSelectedTeamSize(size);
                  setTeamTrialConsent(false);
                }}
                borderColor={colors.border}
                background={colors.surface}
                textColor={colors.text}
              />
              {offerNewTrial ? (
                <div style={{ margin: '1rem 0' }}>
                  <TrialConsentNotice
                    planName="team"
                    agents={selectedTeamSize}
                    profile={profile}
                    checked={teamTrialConsent}
                    onChange={setTeamTrialConsent}
                    colors={colors}
                  />
                </div>
              ) : (
                <div style={{ margin: '1rem 0' }}>
                  <SubscribeConsentNotice
                    planName="team"
                    agents={selectedTeamSize}
                    addon={pickerAddon}
                    refundVariant={chargeRefundVariant}
                    checked={teamTrialConsent}
                    onChange={setTeamTrialConsent}
                    colors={colors}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => handleModalPlanSelection('team', selectedTeamSize)}
                disabled={!teamTrialConsent}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`,
                  color: 'white',
                  cursor: !teamTrialConsent ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  opacity: !teamTrialConsent ? 0.55 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 16px rgba(89, 196, 196, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {offerNewTrial
                  ? trialButtonFor({ planName: 'team', agents: selectedTeamSize, profile })
                  : subscribeButtonFor({ planName: 'team', agents: selectedTeamSize, addon: pickerAddon })}
              </button>
            </div>

            {/* Enterprise Option */}
            <div
              onClick={() => setEnterpriseModal({ ...enterpriseModal, isOpen: true })}
              style={{
                padding: '1rem',
                borderRadius: '12px',
                background: colors.surface,
                textAlign: 'center',
                border: `2px solid ${colors.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = brandColor;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ fontSize: '0.95rem', color: colors.text, marginBottom: '0.5rem' }}>
                <strong>Need 10+ agents?</strong>
              </div>
              <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>
                Contact us for Enterprise pricing →
              </div>
            </div>
          </div>
          </>
          )}

          {/* Sign Out Button */}
          <button
            onClick={handleLogout}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.textSecondary,
              cursor: 'pointer',
              fontSize: '0.9rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = colors.text;
            }}
            onMouseLeave={(e) => {
              e.target.style.color = colors.textSecondary;
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Show loading (wait for both auth and preferences to load)
  if (loading || !preferencesLoaded) {
    return (
      <div className="app-scroll app-scroll-center" style={styles.container}>
        <div style={styles.loginBox}>
          <h1 style={styles.title}>Pacarion</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!client && isHomeownerSignupPath(appPath)) {
    if (isNativeApp()) {
      return (
        <div className="app-scroll app-scroll-center auth-screen" style={{ ...styles.container, background: colors.background }}>
          <HomeownerSignup native />
        </div>
      );
    }
    return (
      <div className="auth-screen">
      <HomeownerSignup
        native={false}
        onSignIn={() => {
          window.history.pushState({}, '', '/login');
          setAppPath('/login');
          setUserType('client');
          setClientLoginMode('password');
        }}
        onSignedUp={async (result) => {
          const data = result && result.client;
          if (!data) return;
          await setClientId(data.id);
          setClient(data);
          setUserType('client');
          const sponsored = Boolean(data.agent_id || data.assigned_agent_id || result.joinRequest);
          setAwaitingQuestionnaire(false);
          setShowSignupPaywall(!sponsored && !isNativeApp());
          setShowOnboarding(sponsored ? showPropertySetup(data) : false);
          window.history.pushState({}, '', '/');
          setAppPath('/');
        }}
      />
      </div>
    );
  }

  // Show unified login page
  if (true) {
    return (
      <div className="app-scroll app-scroll-center auth-screen" style={{...styles.container, background: colors.background}}>
        <div style={{...styles.loginBox, background: colors.surface, border: `1px solid ${colors.border}`}}>
          {!isNativeApp() && (
            <div style={{display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem'}}>
              <a
                href={MARKETING_SITE}
                style={{...styles.backButton, color: colors.textSecondary, textDecoration: 'none'}}
              >
                ← Back
              </a>
            </div>
          )}
          <div style={{textAlign: 'center', margin: '2rem 0'}}>
            <img
              src={isDark ? '/images/logo/pacarion-logo-dark.png' : '/images/logo/pacarion-logo-light.png'}
              alt="Pacarion"
              style={{height: '80px', width: 'auto', marginBottom: '0.5rem'}}
            />
            <p style={{...styles.subtitle, color: colors.textSecondary, margin: 0}}>Home maintenance, made simple</p>
          </div>

          {accountDeleted && (
            <p role="status" style={{color: colors.text, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.85rem 1rem', margin: '0 0 1rem', textAlign: 'center'}}>
              {DELETED_ACCOUNT_MESSAGE}
            </p>
          )}

          {/* User Type Toggle */}
          <div style={{...styles.userTypeToggle, background: colors.surfaceAlt, borderColor: colors.borderLight}}>
            <button
              type="button"
              aria-pressed={userType === 'agent'}
              style={{
                ...styles.toggleButton,
                color: userType === 'agent' ? colors.primary : colors.textSecondary,
                ...(userType === 'agent' ? {...styles.toggleButtonActive, background: colors.surface, color: colors.primary} : {})
              }}
              onClick={() => setUserType('agent')}
            >
              Agent Login
            </button>
            <button
              type="button"
              aria-pressed={userType === 'client'}
              style={{
                ...styles.toggleButton,
                color: userType === 'client' ? colors.primary : colors.textSecondary,
                ...(userType === 'client' ? {...styles.toggleButtonActive, background: colors.surface, color: colors.primary} : {})
              }}
              onClick={() => setUserType('client')}
            >
              Homeowner Login
            </button>
          </div>

          {!isNativeApp() && (
            <p style={{ ...styles.switchMode, color: colors.textSecondary, marginTop: '0.75rem' }}>
              <a
                href="/homeowner/signup"
                style={{ ...styles.link, color: colors.primary, borderBottomColor: colors.primary }}
                onClick={(event) => {
                  event.preventDefault();
                  window.history.pushState({}, '', '/homeowner/signup');
                  setAppPath('/homeowner/signup');
                }}
              >
                Homeowners: sign up
              </a>
            </p>
          )}

          {userType === 'client' && clientLoginMode === 'invite' ? null : (
            <p style={{...styles.switchMode, color: colors.textSecondary, marginTop: 0}}>
              <span
                style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                onClick={() => {
                  setUserType('client');
                  setClientLoginMode('invite');
                  setLoginMode('signin');
                }}
              >
                I got an invite
              </span>
            </p>
          )}

          {/* Client Login */}
          {userType === 'client' ? (
            clientLoginMode === 'invite' ? (
              <form onSubmit={handleClientLogin}>
                <p style={{...styles.instruction, color: colors.textSecondary}}>I got an invite</p>
                <p style={{...styles.instruction, color: colors.textSecondary}}>Enter your invite code</p>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Invite Code"
                  style={{...styles.input, textTransform: 'uppercase', letterSpacing: '0.2em', textAlign: 'center', background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                  maxLength={6}
                  required
                />
                <input
                  type="password"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  placeholder="Password, if you have set one"
                  style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                />
                <button type="submit" style={styles.button}>
                  Access My Home
                </button>
                <p style={{...styles.switchMode, color: colors.textSecondary}}>
                  Already have an account?{' '}
                  <span
                    style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                    onClick={() => setClientLoginMode('password')}
                  >
                    Sign in with email
                  </span>
                </p>
              </form>
            ) : (
              <form onSubmit={handleClientPasswordLogin}>
                <input
                  type="email"
                  value={clientFormData.email}
                  onChange={(e) => {
                    setClientSignInError('');
                    setClientFormData({ ...clientFormData, email: e.target.value });
                  }}
                  placeholder="Email"
                  style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                  required
                />
                <input
                  type="password"
                  value={clientFormData.password}
                  onChange={(e) => {
                    setClientSignInError('');
                    setClientFormData({ ...clientFormData, password: e.target.value });
                  }}
                  placeholder="Password"
                  style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                  required
                />
                {clientSignInError ? (
                  <p role="alert" style={{ color: '#991b1b', margin: '0 0 0.75rem', textAlign: 'left' }}>{clientSignInError}</p>
                ) : null}
                <button type="submit" style={styles.button}>
                  Sign In
                </button>
                <p style={{...styles.forgotPassword, color: colors.textSecondary}}>
                  <span
                    style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                    onClick={() => {
                      setForgotPasswordType('client');
                      setShowForgotPassword(true);
                    }}
                  >
                    Forgot password?
                  </span>
                </p>
                <p style={{...styles.switchMode, color: colors.textSecondary}}>
                  First time here?{' '}
                  <span
                    style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                    onClick={() => setClientLoginMode('invite')}
                  >
                    Use invite code
                  </span>
                </p>
              </form>
            )
          ) : loginMode === 'signin' ? (
            <form onSubmit={handleSignIn}>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setSignInError('');
                  setFormData({ ...formData, email: e.target.value });
                }}
                placeholder="Email"
                style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                required
              />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => {
                  setSignInError('');
                  setFormData({ ...formData, password: e.target.value });
                }}
                placeholder="Password"
                style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                required
              />
              {teamInviteCode && (
                <p style={{fontSize: '0.85rem', color: colors.primary, marginTop: '-0.5rem', marginBottom: '1rem', textAlign: 'left'}}>
                  Signing in with invite code {teamInviteCode} adds you to that team.
                </p>
              )}
              {signInError ? (
                <p role="alert" style={{ color: '#991b1b', margin: '0 0 0.75rem', textAlign: 'left' }}>{signInError}</p>
              ) : null}
              <button type="submit" style={styles.button}>
                Sign In
              </button>
              <p style={{...styles.forgotPassword, color: colors.textSecondary}}>
                <span
                  style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                  onClick={() => {
                    setForgotPasswordType('agent');
                    setShowForgotPassword(true);
                  }}
                >
                  Forgot password?
                </span>
              </p>
              {isNativeApp() ? (
                <p style={{...styles.switchMode, color: colors.textSecondary}}>
                  {NATIVE_PLAN_NOTICE}
                </p>
              ) : (
              <p style={{...styles.switchMode, color: colors.textSecondary}}>
                Don't have an account?{' '}
                <span
                  style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                  onClick={() => setLoginMode('signup')}
                >
                  Sign Up
                </span>
              </p>
              )}
            </form>
          ) : isNativeApp() && !urlInviteCode ? (
            <p style={{color: colors.text, textAlign: 'center', lineHeight: 1.6}}>
              {NATIVE_PLAN_NOTICE}
            </p>
          ) : (
            <form onSubmit={signupStep === 'account' && !urlInviteCode ? handleSignupAccount : handleSignUp}>
              {signupStep === 'account' ? (
                <>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Name"
                    autoComplete="name"
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    required
                  />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Email"
                    autoComplete="email"
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    required
                  />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Password (min 8 characters)"
                    autoComplete="new-password"
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    minLength={8}
                    required
                  />
                  <input
                    type="text"
                    value={formData.market}
                    onChange={(e) => setFormData({ ...formData, market: e.target.value })}
                    placeholder="Market"
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    required
                  />
                </>
              ) : (
                <>
                  <p style={{margin: '0 0 1rem', color: colors.text, textAlign: 'left', fontWeight: '600'}}>
                    Team, if you want one
                  </p>
                  <p style={{margin: '0 0 1rem', color: colors.textSecondary, textAlign: 'left', fontSize: '0.9rem'}}>
                    Join a team or create one. You can also skip this and do it later.
                  </p>
                  <label style={{display: 'block', textAlign: 'left', color: colors.text, fontSize: '0.9rem', marginBottom: '0.35rem'}}>
                    Joining a team? Enter code
                  </label>
                  <input
                    type="text"
                    value={teamInviteCode}
                    onChange={(e) => setTeamInviteCode(e.target.value.toUpperCase())}
                    placeholder="Team code"
                    style={{...styles.input, textTransform: 'uppercase', letterSpacing: '0.1em', background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                    maxLength={8}
                  />
                  <label style={{display: 'block', textAlign: 'left', color: colors.text, fontSize: '0.9rem', marginBottom: '0.35rem'}}>
                    Or create a team
                  </label>
                  <input
                    type="text"
                    value={formData.teamName}
                    onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
                    placeholder="Team name"
                    style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
                  />
                </>
              )}

              {signupStep === 'account' && (
              <>
              <div style={{...styles.checkboxContainer, color: colors.text}}>
                <input
                  type="checkbox"
                  id="termsCheckbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  style={styles.checkbox}
                  required
                />
                <label htmlFor="termsCheckbox" style={{...styles.checkboxLabel, color: colors.text}}>
                  I agree to the{' '}
                  <a
                    href="/terms"
                    onClick={followPolicyLink}
                    style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    onClick={followPolicyLink}
                    style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              {/* A team invite does not start a card trial. */}
              {!urlInviteCode && (
              <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <TrialConsentNotice
                  planName={selectedPlan === 'team' ? 'team' : 'solo'}
                  agents={selectedPlan === 'team' ? selectedTeamSize : 1}
                  checked={trialConsent}
                  onChange={setTrialConsent}
                  colors={colors}
                />
              </div>
              )}
              </>
              )}

              {signupError && (
                <p style={{color: '#b91c1c', fontSize: '0.9rem', textAlign: 'left', marginTop: '0.5rem'}}>
                  {signupError}
                </p>
              )}

              {signupNotice === 'existing' && (
                <ExistingAccountNotice
                  onSignIn={() => {
                    setSignupNotice(null);
                    setLoginMode('signin');
                  }}
                  onForgotPassword={() => {
                    setForgotPasswordType('agent');
                    setShowForgotPassword(true);
                  }}
                />
              )}

              {signupStep === 'account' && !urlInviteCode ? (
                <button type="submit" style={styles.button} disabled={!trialConsent}>Continue</button>
              ) : signupStep === 'account' ? (
                <button type="submit" style={styles.button}>Create Account</button>
              ) : (
                <>
                  <button type="submit" name="skip-team" onClick={() => { skipTeamRef.current = true; }} style={{...styles.button, background: colors.surfaceAlt, color: colors.text, border: `2px solid ${colors.border}`}}>
                    Skip for now
                  </button>
                  <button type="submit" style={styles.button}>
                    {urlInviteCode || !selectedPlan ? 'Create Account' : 'Start Free Trial'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSignupError(''); setSignupStep('account'); }}
                    style={{...styles.switchMode, background: 'transparent', border: 'none', color: colors.primary, cursor: 'pointer', width: '100%'}}
                  >
                    Back
                  </button>
                </>
              )}
              <p style={{...styles.switchMode, color: colors.textSecondary}}>
                Already have an account?{' '}
                <span
                  style={{...styles.link, color: colors.primary, borderBottomColor: colors.primary}}
                  onClick={() => setLoginMode('signin')}
                >
                  Sign In
                </span>
              </p>
            </form>
          )}
        </div>
        <Footer />
        {showForgotPassword && (
          <ForgotPassword
            onClose={() => setShowForgotPassword(false)}
            userType={forgotPasswordType}
          />
        )}

        {/* Subscription Modal - Global overlay for subscription management */}
        <SubscriptionModal
          isOpen={subscriptionModal.isOpen}
          type={subscriptionModal.type}
          plan={subscriptionModal.plan}
          agents={subscriptionModal.agents}
          error={subscriptionModal.error}
          onClose={handleModalClose}
          onConfirm={subscriptionModal.type === 'choose-plan' ? handleModalPlanSelection :
                     subscriptionModal.type === 'checkout-canceled' ? handleModalPlanSelection :
                     null}
        />

        {/* Enterprise Contact Modal */}
        {enterpriseModal.isOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}>
            <div style={{
              background: colors.background,
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{ margin: 0, color: colors.text }}>Enterprise Inquiry</h2>
                <button
                  onClick={() => setEnterpriseModal({ isOpen: false, submitting: false })}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    color: colors.textSecondary,
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              </div>

              <p style={{ color: colors.textSecondary, marginBottom: '1.5rem' }}>
                Looking for a custom solution for 10+ agents? Fill out the form below and our team will contact you within 24 hours to discuss Enterprise pricing and features.
              </p>

              <form onSubmit={handleEnterpriseSubmit}>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={enterpriseFormData.firstName}
                      onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, firstName: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: `2px solid ${colors.border}`,
                        background: colors.background,
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={enterpriseFormData.lastName}
                      onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, lastName: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: `2px solid ${colors.border}`,
                        background: colors.background,
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={enterpriseFormData.email}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, email: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `2px solid ${colors.border}`,
                      background: colors.background,
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={enterpriseFormData.company}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, company: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `2px solid ${colors.border}`,
                      background: colors.background,
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                      Number of Agents *
                    </label>
                    <input
                      type="number"
                      value={enterpriseFormData.agentCount}
                      onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, agentCount: e.target.value })}
                      min="11"
                      required
                      placeholder="11+"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: `2px solid ${colors.border}`,
                        background: colors.background,
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={enterpriseFormData.phone}
                      onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: `2px solid ${colors.border}`,
                        background: colors.background,
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 500 }}>
                    Message / Additional Details
                  </label>
                  <textarea
                    value={enterpriseFormData.message}
                    onChange={(e) => setEnterpriseFormData({ ...enterpriseFormData, message: e.target.value })}
                    rows="4"
                    placeholder="Tell us about your needs..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `2px solid ${colors.border}`,
                      background: colors.background,
                      color: colors.text,
                      fontSize: '1rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setEnterpriseModal({ isOpen: false, submitting: false })}
                    disabled={enterpriseModal.submitting}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: `2px solid ${colors.border}`,
                      background: 'transparent',
                      color: colors.text,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={enterpriseModal.submitting}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: enterpriseModal.submitting ? '#999' : `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`,
                      color: 'white',
                      fontSize: '1rem',
                      cursor: enterpriseModal.submitting ? 'not-allowed' : 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {enterpriseModal.submitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

function AppRoot() {
  return (
    <>
      <App />
      <ToastContainer />
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    paddingTop: 'calc(2rem + env(safe-area-inset-top, 0px))',
    paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
    paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))'
  },
  loginBox: {
    padding: window.innerWidth >= 768 ? '3rem' : '2rem',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: window.innerWidth >= 768 ? '600px' : '440px',
    width: '100%'
  },
  title: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    fontWeight: 300,
    letterSpacing: '0.05em'
  },
  subtitle: {
    fontSize: '0.9rem',
    marginBottom: '2rem',
    fontWeight: 300,
    letterSpacing: '0.03em'
  },
  userTypeToggle: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    borderRadius: '12px',
    padding: '0.375rem',
    border: '1px solid'
  },
  toggleButton: {
    flex: 1,
    padding: '0.75rem',
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontWeight: 400,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  toggleButtonActive: {
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  instruction: {
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontWeight: 300,
    letterSpacing: '0.02em'
  },
  input: {
    width: '100%',
    padding: '0.9rem 1.1rem',
    fontSize: '1rem',
    borderRadius: '12px',
    marginBottom: '1rem',
    textAlign: 'left',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  },
  button: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 400,
    color: 'white',
    background: '#59c4c4',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    marginBottom: '1rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)'
  },
  switchMode: {
    fontSize: '0.9rem',
    fontWeight: 300,
    letterSpacing: '0.02em'
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 400,
    borderBottom: '1px solid'
  },
  themeToggleBtn: {
    background: 'transparent',
    border: '1px solid',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '0.85rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  backButton: {
    background: 'transparent',
    border: 'none',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontWeight: 300,
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '1rem',
    textAlign: 'left'
  },
  checkbox: {
    marginTop: '0.25rem',
    cursor: 'pointer',
    width: '18px',
    height: '18px',
    flexShrink: 0
  },
  checkboxLabel: {
    fontSize: '0.85rem',
    fontWeight: 300,
    lineHeight: '1.6',
    letterSpacing: '0.02em',
    cursor: 'pointer'
  },
  forgotPassword: {
    fontSize: '0.85rem',
    fontWeight: 300,
    letterSpacing: '0.02em',
    textAlign: 'center',
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  }
};

export default AppRoot;
